// src/redux/slices/rulesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import rulesService from '../../services/rulesService'
import { addAuditLog } from './auditSlice'

export const safeDate = (iso) => {
  if (!iso) return 'Never'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? 'Never' : d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit',
  })
}


// ── Thunks ────────────────────────────────────────────────────────────────

export const fetchRules = createAsyncThunk('rules/fetchList',
  async (params, { rejectWithValue }) => {
    try { return await rulesService.getList(params) }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const fetchRuleById = createAsyncThunk('rules/fetchById',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await rulesService.getById(id)
      dispatch(addAuditLog({ action: 'RULE_VIEWED', subject: 'Rule', meta: { ruleId: id } }))
      return data
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const createRule = createAsyncThunk('rules/create',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await rulesService.create(payload)
      dispatch(addAuditLog({ action: 'RULE_CREATED', subject: 'Rule', meta: { ruleId: data.id, ruleName: data.name } }))
      return data
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const updateRule = createAsyncThunk('rules/update',
  async ({ id, payload }, { dispatch, rejectWithValue }) => {
    try {
      const data = await rulesService.update(id, payload)
      dispatch(addAuditLog({ action: 'RULE_UPDATED', subject: 'Rule', meta: { ruleId: id, version: data.version } }))
      return data
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const duplicateRule = createAsyncThunk('rules/duplicate',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await rulesService.duplicate(id)
      dispatch(addAuditLog({ action: 'RULE_DUPLICATED', subject: 'Rule', meta: { sourceId: id, newId: data.id } }))
      return data
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const toggleRuleStatus = createAsyncThunk('rules/toggleStatus',
  async ({ id, status }, { dispatch, rejectWithValue }) => {
    try {
      const data = await rulesService.toggleStatus(id, status)
      dispatch(addAuditLog({ action: `RULE_${status}`, subject: 'Rule', meta: { ruleId: id } }))
      return data
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const archiveRule = createAsyncThunk('rules/archive',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await rulesService.archive(id)
      dispatch(addAuditLog({ action: 'RULE_ARCHIVED', subject: 'Rule', meta: { ruleId: id } }))
      return id
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const fetchRuleVersions = createAsyncThunk('rules/fetchVersions',
  async (id, { rejectWithValue }) => {
    try { return await rulesService.getVersions(id) }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const rollbackRule = createAsyncThunk('rules/rollback',
  async ({ id, version, reason }, { dispatch, rejectWithValue }) => {
    try {
      const data = await rulesService.rollback(id, version, reason)
      dispatch(addAuditLog({ action: 'RULE_ROLLED_BACK', subject: 'Rule', meta: { ruleId: id, toVersion: version } }))
      return data
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const runSandbox = createAsyncThunk('rules/runSandbox',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      // payload from component: { mode, ruleId, txnPayload }
      // backend SandboxRequest:  { mode, ruleId, txnPayload }
      // mode: component sends 'active'|'single'|'all'
      //       backend accepts 'active'|'rule'|'all' — 'single' handled by backend now
      const data = await rulesService.evaluate({
        mode:       payload.mode       || 'active',
        ruleId:     payload.ruleId     || null,
        txnPayload: payload.txnPayload || {},
      })
      dispatch(addAuditLog({
        action: 'SANDBOX_RUN', subject: 'Rule',
        meta: { mode: payload.mode, ruleId: payload.ruleId },
      }))
      return data
    }
    catch (e) {
      const detail = e?.response?.data?.detail
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || String(d)).join('; ')
        : detail || e?.message || 'Evaluation failed'
      return rejectWithValue(msg)
    }
  }
)

export const runSandboxCompare = createAsyncThunk('rules/runSandboxCompare',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await rulesService.compare(payload)
      dispatch(addAuditLog({ action: 'SANDBOX_COMPARE', subject: 'Rule', meta: { mode: 'compare' } }))
      return data
    }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

export const saveTestCase = createAsyncThunk('rules/saveTestCase',
  async (payload, { rejectWithValue }) => {
    try { return await rulesService.saveTestCase(payload) }
    catch (e) { return rejectWithValue(e.response?.data?.message || e.message) }
  }
)

// ── Slice ─────────────────────────────────────────────────────────────────

const rulesSlice = createSlice({
  name: 'rules',
  initialState: {
    list:            [],
    summary:         { active: 0, inactive: 0, totalTriggers: 0, mostTriggered: [], byCategory: {} },
    selected:        null,
    versions:        [],
    sandboxResult:   null,
    compareResult:   null,
    savedTestCases:  [],
    loading: {
      list:     false,
      detail:   false,
      action:   false,
      versions: false,
      sandbox:  false,
      compare:  false,
    },
    errors: {},
    filters: { status: '', category: '', severity: '', search: '' },
  },
  reducers: {
    setRuleFilter(state, { payload })    { state.filters = { ...state.filters, ...payload } },
    resetRuleFilters(state)              { state.filters = { status: '', category: '', severity: '', search: '' } },
    clearSandboxResult(state)           { state.sandboxResult = null },
    clearCompareResult(state)           { state.compareResult = null },
    clearSelected(state)                { state.selected = null },
    addSavedTestCase(state, { payload }) { state.savedTestCases = [payload, ...state.savedTestCases] },
  },
  extraReducers: (builder) => {
    builder
      // list
      .addCase(fetchRules.pending,   s    => { s.loading.list = true;  s.errors.list = null })
      .addCase(fetchRules.rejected,  (s,a)=> { s.loading.list = false; s.errors.list = a.payload })
      .addCase(fetchRules.fulfilled, (s,a)=> { s.loading.list = false; s.list = a.payload.data; s.summary = a.payload.summary || s.summary })
      // detail
      .addCase(fetchRuleById.pending,   s    => { s.loading.detail = true;  s.errors.detail = null })
      .addCase(fetchRuleById.rejected,  (s,a)=> { s.loading.detail = false; s.errors.detail = a.payload })
      .addCase(fetchRuleById.fulfilled, (s,a)=> { s.loading.detail = false; s.selected = a.payload })
      // create
      .addCase(createRule.pending,   s    => { s.loading.action = true;  s.errors.action = null })
      .addCase(createRule.rejected,  (s,a)=> { s.loading.action = false; s.errors.action = a.payload })
      .addCase(createRule.fulfilled, (s,a)=> { s.loading.action = false; s.list = [a.payload, ...s.list] })
      // update
      .addCase(updateRule.pending,   s    => { s.loading.action = true;  s.errors.action = null })
      .addCase(updateRule.rejected,  (s,a)=> { s.loading.action = false; s.errors.action = a.payload })
      .addCase(updateRule.fulfilled, (s,a)=> { s.loading.action = false; s.list = s.list.map(r => r.id === a.payload.id ? a.payload : r); s.selected = a.payload })
      // duplicate
      .addCase(duplicateRule.fulfilled, (s,a)=> { s.list = [a.payload, ...s.list] })
      // toggle
      .addCase(toggleRuleStatus.fulfilled, (s,a)=> { s.list = s.list.map(r => r.id === a.payload.id ? a.payload : r); if (s.selected?.id === a.payload.id) s.selected = a.payload })
      // archive
      .addCase(archiveRule.fulfilled, (s,a)=> { s.list = s.list.filter(r => r.id !== a.payload) })
      // versions
      .addCase(fetchRuleVersions.pending,   s    => { s.loading.versions = true })
      .addCase(fetchRuleVersions.rejected,  (s,a)=> { s.loading.versions = false; s.errors.versions = a.payload })
      .addCase(fetchRuleVersions.fulfilled, (s,a)=> { s.loading.versions = false; s.versions = a.payload.versions || [] })
      // rollback
      .addCase(rollbackRule.fulfilled, (s,a)=> { s.list = s.list.map(r => r.id === a.payload.id ? a.payload : r); s.selected = a.payload })
      // sandbox
      .addCase(runSandbox.pending,   s    => { s.loading.sandbox = true;  s.errors.sandbox = null; s.sandboxResult = null })
      .addCase(runSandbox.rejected,  (s,a)=> { s.loading.sandbox = false; s.errors.sandbox = a.payload })
      .addCase(runSandbox.fulfilled, (s, a) => {
        s.loading.sandbox = false
        // Backend SandboxResponse fields:
        // final_decision, risk_score, total_latency_ms, triggered_count, total_rules, timestamp, results[]
        // Component normalizeSandboxResult reads camelCase via ?? fallback so just store as-is
        s.sandboxResult = a.payload
      })
      // compare
      .addCase(runSandboxCompare.pending,   s    => { s.loading.compare = true;  s.errors.compare = null; s.compareResult = null })
      .addCase(runSandboxCompare.rejected,  (s,a)=> { s.loading.compare = false; s.errors.compare = a.payload })
      .addCase(runSandboxCompare.fulfilled, (s,a)=> { s.loading.compare = false; s.compareResult = a.payload })
      // save test case
      .addCase(saveTestCase.fulfilled, (s,a)=> { s.savedTestCases = [a.payload, ...s.savedTestCases] })
  },
})

export const {
  setRuleFilter, resetRuleFilters,
  clearSandboxResult, clearCompareResult,
  clearSelected, addSavedTestCase,
} = rulesSlice.actions

export const selectRuleList      = s => s.rules.list
export const selectRuleSummary   = s => s.rules.summary
export const selectRuleSelected  = s => s.rules.selected
export const selectRuleVersions  = s => s.rules.versions
export const selectSandboxResult = s => s.rules.sandboxResult
export const selectCompareResult = s => s.rules.compareResult
export const selectSavedCases    = s => s.rules.savedTestCases
export const selectRulesLoading  = s => s.rules.loading
export const selectRulesErrors   = s => s.rules.errors
export const selectRuleFilters   = s => s.rules.filters

export default rulesSlice.reducer
